import { APP_PAGE_SIZE_MEDIUM, APP_PAGE_SIZE_SHORT } from "app/appConstants";
import { MprcRequestErrorDialog, MprcSpinnerIndicator } from "components";
import { FeedPanelUserCredentialsResponse, UserCredentialTypeSelectorResponse, UserPanelPersonalInterestDtoPaginatedList, UserPanelProfessionaInterestDtoPaginatedList, UserPanelStatisticDto, UserProfileDto } from "generated/CoreApi_v1";
import LibUtils from "lib/LibUtils";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FeedBoxPanels, FeedDrawerPanels } from "views/knowledge/feed/components/FeedBoxPanels";
import { PanelUserCredentials } from "views/knowledge/feed/components/Panel/PanelUserCredentials";
import { PanelUserOtherInfo } from "views/knowledge/feed/components/Panel/PanelUserOtherInfo";
import { PanelUserPersonalInterest } from "views/knowledge/feed/components/Panel/PanelUserPersonalInterest";
import { PanelUserProfessionalInterest } from "views/knowledge/feed/components/Panel/PanelUserProfessionalInterest";
import { PanelUserSocialNetwork } from "views/knowledge/feed/components/Panel/PanelUserSocialNetwork";
import { PanelUserStatistic } from "views/knowledge/feed/components/Panel/PanelUserStatistic";

interface IProps {
    user: UserProfileDto;
    renderMode?: "default" | "drawer";
    onEmptyPanel: () => void;
}

/**
 * Painel lateral para o feed de perfil do usuário.
 * @param props
 */
export const FeedUserProfileRightPanel: React.FC<IProps> = (props) => {

    const user = props.user!;
    const { userId } = user;
    const { renderMode = "default" } = props;

    const api = useCoreApi();
    const { t } = useTranslation("knowledge");

    const [viewError, setViewError] = useState<any>();
    const [professionalInterest, setProfessionalInterest] = useState<UserPanelProfessionaInterestDtoPaginatedList>();
    const [personalInterest, setPersonalInterest] = useState<UserPanelPersonalInterestDtoPaginatedList>();
    const [userCredentials, setUserCredentials] = useState<FeedPanelUserCredentialsResponse[]>();
    const [credentialTypes, setCredentialTypes] = useState<UserCredentialTypeSelectorResponse[]>([]);
    const [statistic, setStatistic] = useState<UserPanelStatisticDto>();
    const [loading, setLoading] = useState<boolean>(true);

    ////////////////////////////////////////////////////////////////////////////
    /// Effects
    ////////////////////////////////////////////////////////////////////////////

    const customFields = user?.customFields?.filter(x => x.fieldValue != null && (x.fieldValue.length > 0 || LibUtils.isValidDate(x.fieldValue)));

    // Ao carregar
    useEffect(() => {
        if (loading) {
            Promise.all([
                api.v1.knowledgeFeed.getFeedUserPanelProfessionalInterest({ userId, pageNumber: 1, pageSize: APP_PAGE_SIZE_SHORT, searchText: null }),
                api.v1.knowledgeFeed.getFeedUserPanelPersonalInterest({ userId, pageNumber: 1, pageSize: APP_PAGE_SIZE_MEDIUM, searchText: null }),
                api.v1.knowledgeFeed.getFeedPanelUserCredentials({ userId }),
                api.v1.selectors.userCredentialTypes(),
                api.v1.knowledgeFeed.getFeedPanelUserStatistic({ userId })
            ])
                .then((results) => {
                    setProfessionalInterest(results[0].data);
                    setPersonalInterest(results[1].data);
                    setUserCredentials(results[2].data);
                    setCredentialTypes(results[3].data);
                    setStatistic(results[4].data);
                })
                .catch((error: any) => {
                    setViewError(error);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [loading]);


    const panels = useMemo(() => {
        const data = [];

        if (loading) return [];

        if (professionalInterest && professionalInterest.totalRows > 0)
            data.push(
                <PanelUserProfessionalInterest
                    title={t("Feed.Panels.UserProfessionalInterest.Title")}
                    userId={userId}
                    viewData={professionalInterest!}
                    onChange={(item, index) => {
                        let newItems = [...professionalInterest.items];
                        newItems[index] = item;
                        setProfessionalInterest({ ...professionalInterest, items: newItems });
                    }}
                    onGetData={(pageSize) =>
                        new Promise<boolean>((resolve, reject) => {
                            api.v1.knowledgeFeed.getFeedUserPanelProfessionalInterest({ userId, pageNumber: 1, pageSize, searchText: null })
                                .then(result => {
                                    setProfessionalInterest(result.data);
                                    resolve(true);
                                })
                                .catch(e => reject(e));
                        })
                    }
                />
            );


        if (userCredentials && userCredentials.length > 0 && credentialTypes)
            data.push(<PanelUserCredentials viewData={userCredentials} credentialTypes={credentialTypes} />);

        if (user.socialNetworks.filter(p => p.socialNetworkId != "").length > 0)
            data.push(<PanelUserSocialNetwork user={user} />);

        if (user?.birthDate != null || user?.admissionDate != null || user?.dismissedDate != null || (customFields && customFields.length > 0))
            data.push(<PanelUserOtherInfo user={user} />);

        if (statistic && (statistic.totalPublications > 0
            || statistic.totalAnswers > 0
            || statistic.totalViews > 0
            || statistic.totalLikes > 0
            || statistic.followers > 0
            || statistic.following > 0
            || statistic.historyPoints > 0
            || statistic.monthPoints > 0
            || statistic.totalBadges > 0)) {
            data.push(<PanelUserStatistic user={user} viewData={statistic} />);
        }

        if (personalInterest && personalInterest.totalRows > 0)
            data.push(
                <PanelUserPersonalInterest
                    viewData={personalInterest}
                    onGetData={(pageSize) =>
                        new Promise<boolean>((resolve, reject) => {
                            api.v1.knowledgeFeed.getFeedUserPanelPersonalInterest({ userId, pageNumber: 1, pageSize, searchText: null })
                                .then(result => {
                                    setPersonalInterest(result.data);
                                    resolve(true);
                                })
                                .catch(e => reject(e));
                        })
                    }
                />
            );

        if (data.length === 0) props.onEmptyPanel();

        return data;
    }, [professionalInterest, personalInterest, statistic, userCredentials, credentialTypes, props.onEmptyPanel, loading]);


    ////////////////////////////////////////////////////////////////////////////
    /// Render
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            <MprcRequestErrorDialog
                error={viewError}
                onClose={() => setViewError(undefined)}
            />

            {
                loading
                    ? <MprcSpinnerIndicator />
                    : renderMode == "default"
                        ? <FeedBoxPanels>{panels}</FeedBoxPanels>
                        : <FeedDrawerPanels>{panels}</FeedDrawerPanels>
            }
        </>
    );
};
